import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import ConnectionStatus from "./ConnectionStatus";
import {
  Gauge, Package, UsersThree, Warehouse, FileText, ClipboardText, SignOut, ShieldCheck, Hourglass, List, X, Eye,
} from "@phosphor-icons/react";

const MENU = {
  admin: [
    { to: "/admin",          label: "Dashboard",   icon: Gauge },
    { to: "/admin/sessions", label: "Sessions",    icon: Warehouse },
    { to: "/admin/reports",  label: "Reports",     icon: FileText },
    { to: "/admin/users",    label: "Users",       icon: UsersThree },
    { to: "/admin/audit",    label: "Audit Log",   icon: ShieldCheck },
  ],
  counter: [
    { to: "/counter", label: "My Counts", icon: ClipboardText },
  ],
  head_counter: [
    { to: "/head-counter", label: "Escalated Items", icon: Hourglass },
  ],
  alley_head: [
    { to: "/alley-head", label: "Alley Counts", icon: ClipboardText },
  ],
  wh_alley: [
    { to: "/wh-alley", label: "Discrepancies", icon: Eye },
  ],
};

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const menu = MENU[user?.role] || [];
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-zinc-50">
      {/* Top header */}
      <header className="app-header border-b border-zinc-300 bg-white sticky top-0 z-40 no-print">
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 flex items-center justify-between h-14 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {menu.length > 0 && (
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="md:hidden rounded-sm border border-zinc-300 hover:bg-zinc-100 p-1.5 shrink-0"
                aria-label="Open menu"
                data-testid="mobile-menu-button"
              >
                <List className="w-4 h-4" />
              </button>
            )}
            <div className="w-8 h-8 rounded-sm bg-indigo-950 text-white grid place-items-center font-display font-bold shrink-0">IR</div>
            <div className="leading-tight min-w-0 hidden sm:block">
              <div className="font-display font-semibold text-zinc-900 text-[15px] truncate" data-testid="app-title">Inventory Reconciliation</div>
              <div className="text-[11px] text-zinc-500 tracking-wider uppercase">Warehouse Operations</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <ConnectionStatus />
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm text-zinc-900 truncate max-w-[140px]" data-testid="current-user-name">{user?.full_name}</span>
              <span className="text-[11px] uppercase tracking-wider text-zinc-500" data-testid="current-user-role">{user?.role?.replace("_", " ")}</span>
            </div>
            <button
              onClick={async () => { await logout(); navigate("/login"); }}
              className="inline-flex items-center gap-1.5 rounded-sm border border-zinc-300 bg-white hover:bg-zinc-100 text-zinc-800 px-2 sm:px-3 py-1.5 text-xs sm:text-sm transition-colors"
              data-testid="logout-button"
            >
              <SignOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        {/* Sidebar — desktop */}
        {menu.length > 0 && (
          <aside className="w-56 shrink-0 border-r border-zinc-300 bg-white no-print hidden md:flex flex-col">
            <nav className="flex-1 py-4">
              {menu.map((m) => (
                <NavLink key={m.to} to={m.to} end
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-5 py-2.5 text-sm transition-colors border-l-2 ${isActive ? "bg-zinc-100 border-indigo-900 text-zinc-900 font-medium" : "border-transparent text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"}`
                  }
                  data-testid={`nav-${m.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <m.icon className="w-4 h-4" /> {m.label}
                </NavLink>
              ))}
            </nav>
            <div className="px-5 py-3 text-[11px] text-zinc-500 border-t border-zinc-200">
              v1.0 · Built for warehouse ops
            </div>
          </aside>
        )}

        {/* Sidebar — mobile drawer */}
        {menu.length > 0 && mobileMenuOpen && (
          <div className="md:hidden fixed inset-0 z-50 flex">
            <div className="absolute inset-0 bg-zinc-950/60" onClick={() => setMobileMenuOpen(false)} />
            <div className="relative w-64 max-w-[80vw] bg-white border-r border-zinc-300 shadow-xl flex flex-col">
              <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
                <div className="font-display font-semibold text-zinc-900">Menu</div>
                <button onClick={() => setMobileMenuOpen(false)} className="rounded-sm hover:bg-zinc-100 p-1" aria-label="Close"><X className="w-4 h-4" /></button>
              </div>
              <nav className="flex-1 py-3">
                {menu.map((m) => (
                  <NavLink key={m.to} to={m.to} end onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-5 py-2.5 text-sm border-l-2 ${isActive ? "bg-zinc-100 border-indigo-900 text-zinc-900 font-medium" : "border-transparent text-zinc-700"}`
                    }>
                    <m.icon className="w-4 h-4" /> {m.label}
                  </NavLink>
                ))}
              </nav>
              <div className="px-5 py-3 text-[11px] text-zinc-500 border-t border-zinc-200">
                {user?.full_name} · {user?.role?.replace("_", " ")}
              </div>
            </div>
          </div>
        )}

        <main className="flex-1 min-w-0 print-full-width">
          {children}
        </main>
      </div>
    </div>
  );
}
